import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { Store, select } from '@ngrx/store'
import { AccountObject, AccountType, DistributionListObject } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { uniq } from 'lodash-es'
import { combineLatest } from 'rxjs'
import { map, distinctUntilChanged, startWith, pairwise } from 'rxjs/operators'
import { ApiContactsQueryTrigger } from '@tradecafe/notification-service-sdk-ng'
import { selectAllAccounts } from 'src/app/store/accounts'
import { selectAllLocations } from 'src/app/store/locations'
import { selectAllProductCategories } from 'src/app/store/product-categories/product-category.selectors'
import { selectAllProductTypes } from 'src/app/store/product-types/product-type.selectors'
import { selectAllProducts } from 'src/app/store/products/product.selectors'
import { selectAllUsers } from 'src/app/store/users'
import { waitNotEmpty } from 'src/services/data/utils'
import { isCompanyVisible } from 'src/services/data/accounts.service'
import { replayForm } from 'src/shared/utils/replay-form'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { SelectSearchComponent } from 'src/components/select-search/select-search.component'

export const createDistList = (): DistributionListObject => ({
  name: '',
  filters: {
    locations: [],
    suppliers: [],
    providers: [],
    products: [],
    product_categories: [],
    product_types: [],
  },
  notes: '',
  recipients: [],
  service_providers: [],
  contacts: {
    buyer: [],
    service_provider: [],
  },
  updated_at: 0,
  updated_by: '',
})

@Component({
  selector: 'tc-distribution-list',
  templateUrl: './distribution-list.component.html',
  styleUrls: ['./distribution-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class DistributionListComponent extends OnDestroyMixin implements OnInit {
  private store = inject(Store)

  @Input() company: DeepReadonly<AccountObject>
  @Input() distributionList: DeepReadonly<DistributionListObject>

  // subscriptions
  protected locations$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllLocations), waitNotEmpty())
  protected accounts$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllAccounts), waitNotEmpty())
  protected buyers$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllAccounts), waitNotEmpty(),
      map(account => account.filter((ac) => ac.type === AccountType.BUYER)))
  protected suppliers$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllAccounts), waitNotEmpty(),
      map(account => account.filter((ac) => ac.type === AccountType.SUPPLIER)))
  protected serviceProviders$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllAccounts), waitNotEmpty(),
      map(account => account.filter((ac) => ac.type === AccountType.SERVICE_PROVIDER)))
  protected contactCompanies$ = this.accounts$.pipe(map(accounts => accounts.filter(isCompanyVisible)))
  protected productCategories$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllProductCategories), waitNotEmpty())
  protected productTypes$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllProductTypes), waitNotEmpty())
  protected products$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllProducts), waitNotEmpty())
  protected users$ = this.store.pipe(untilComponentDestroyed(this), select(selectAllUsers), waitNotEmpty())
  //
  protected accounts = []
  protected allProductCategories = []
  protected allProductTypes = []
  protected allProducts = []
  protected filteredProductCategories = []
  protected filteredProductTypes = []
  protected filteredProducts = []

  @ViewChild('serviceProviderSelect', { static: true })
  protected serviceProviderSelect: SelectSearchComponent<AccountObject[], number[]>;

  protected distributionListForm = new FormGroup(
    {
      name: new FormControl<string>(undefined, { validators: [Validators.required] }),
      filter_locationIds: new FormControl<DeepReadonly<string[]>>([]),
      filter_supplierIds: new FormControl<DeepReadonly<number[]>>([]),
      filter_serviceProviderIds: new FormControl<DeepReadonly<number[]>>([]),
      filter_productCategoryIds: new FormControl<DeepReadonly<string[]>>([]),
      filter_productTypeIds: new FormControl<DeepReadonly<string[]>>([]),
      filter_productIds: new FormControl<DeepReadonly<string[]>>([]),
      buyer_contacts: new FormControl<DeepReadonly<string[]>>([]),
      service_providers: new FormControl<DeepReadonly<number[]>>([]),
      service_provider_contacts: new FormControl<DeepReadonly<string[]>>([]),
      notes: new FormControl<string>(undefined),
    },
  )

  protected filteredBuyerContacts$ = combineLatest([this.users$, replayForm(this.distributionListForm.controls.buyer_contacts)])
    .pipe(
      untilComponentDestroyed(this),
      distinctUntilChanged(),
      map(([users, contacts]) => users.filter((user) => (!user.archived || contacts.includes(user.user_id)) &&
    user.account === this.company.account)
  ));

  protected readonly filteredServiceProviderContacts$ = combineLatest([
    replayForm(this.distributionListForm.controls.service_providers),
    this.users$,
    replayForm(this.distributionListForm.controls.service_provider_contacts),
  ]).pipe(
      untilComponentDestroyed(this),
      distinctUntilChanged(),
      map(([service_providers, users, contacts]) =>
        users.filter((user) => {
          if (contacts.includes(user.user_id)) {
            return true;
          }

          if (user.archived) {
            return false;
          }

          return service_providers.includes(user.account)
        })
  ));

  protected notificationTrigger$ = this.distributionListForm.valueChanges.pipe(
    untilComponentDestroyed(this),
    startWith(this.distributionListForm.value),
    map(() => this.buildNotificationTrigger()),
    distinctUntilChanged()
  );

  // tslint:disable-next-line:cyclomatic-complexity
  ngOnInit(): void {
    if (!this.distributionList) {
      this.distributionList = createDistList()
    }
    // assign dropdown options for product-category / product-type / product
    this.productCategories$.subscribe((pcs) => {
      this.allProductCategories = pcs
      this.filteredProductCategories = this.allProductCategories
    })
    this.productTypes$.subscribe((pts) => {
      this.allProductTypes = pts
      this.filteredProductTypes = this.allProductTypes
    })
    this.products$.subscribe((ps) => {
      this.allProducts = ps
      this.filteredProducts = this.allProducts
    })
    this.accounts$.subscribe((accs) => {
      this.accounts = accs
    })
    this.distributionListForm.patchValue(
      {
        name: this.distributionList?.name || '',
        filter_locationIds: this.distributionList?.filters.locations || [],
        filter_supplierIds: this.distributionList?.filters.suppliers.map((s) => parseFloat(s)) || [],
        filter_serviceProviderIds: this.distributionList?.filters.providers.map((s) => parseFloat(s)) || [],
        filter_productCategoryIds: this.distributionList?.filters.product_categories || [],
        filter_productTypeIds: this.distributionList?.filters.product_types || [],
        filter_productIds: this.distributionList?.filters.products || [],
        notes: this.distributionList?.notes || ' ',
      },
    )
    this.initContacts()

    combineLatest([
      this.serviceProviderSelect.valueChanges,
      this.users$,
    ]).pipe(
      untilComponentDestroyed(this),
      distinctUntilChanged(),
    ).subscribe(([serviceProviders, users]) => {
      const contacts = this.distributionListForm.controls.service_provider_contacts.value.filter((c) => {
        const user = users.find((u) => u.user_id === c)

        return serviceProviders.includes(user?.account)
      })

      this.distributionListForm.controls.service_provider_contacts.setValue(contacts);
    });
  }

  initContacts() {
    this.distributionListForm.patchValue({ service_providers: this.distributionList.service_providers || [] })
    this.distributionListForm.patchValue({ buyer_contacts: this.distributionList.contacts?.buyer || [] })
    this.distributionListForm.patchValue({ service_provider_contacts: this.distributionList.contacts?.service_provider || [] })
  }

  onProductCategoriesChanged() {
    let pts = []
    this.distributionListForm.controls.filter_productCategoryIds.value.forEach((category_id) => {
      pts = pts.concat(this.allProductTypes.filter((pt) => pt.category_id === category_id))

    })
    this.filteredProductTypes = pts
  }

  onProductTypesChanged() {
    let ps = []
    this.distributionListForm.controls.filter_productCategoryIds.value.forEach((category_id) => {
      ps = ps.concat(this.allProducts.filter((p) => p.category_id === category_id))
    })
    this.distributionListForm.controls.filter_productTypeIds.value.forEach((type_id) => {
      ps = ps.concat(this.allProducts.filter((p) => p.type_id === type_id))
    })
    this.filteredProducts = ps
  }

  getDistributionList() {
    return {
      name: this.distributionListForm.controls.name.value,
      filters: {
        locations: this.distributionListForm.controls.filter_locationIds.value,
        suppliers: this.distributionListForm.controls.filter_supplierIds.value.map((s) => `${s}`),
        providers: this.distributionListForm.controls.filter_serviceProviderIds.value.map((s) => `${s}`),
        products: this.distributionListForm.controls.filter_productIds.value,
        product_categories: this.distributionListForm.controls.filter_productCategoryIds.value,
        product_types: this.distributionListForm.controls.filter_productTypeIds.value,
      },
      notes: this.distributionListForm.controls.notes.value,
      recipients: Array.isArray(this.distributionList.recipients) ? this.distributionList.recipients : [],
      service_providers: this.distributionListForm.controls.service_providers.value,
      contacts: {
        buyer: this.distributionListForm.controls.buyer_contacts.value,
        service_provider: this.distributionListForm.controls.service_provider_contacts.value,
      },
    }
  }

  buildNotificationTrigger(): ApiContactsQueryTrigger {
    const distList = this.getDistributionList()

    return {
      event: 'tc-ns-segment-updated',
      attributes: {
        type: 'email',
        recipient_user_types: []
      },
      companies: this.company ? [this.company.account] : [],
      users: uniq([
        ...(distList.contacts.buyer || []),
        ...(distList.contacts.service_provider || [])
      ]),
    }
  }
}
